'use client';

import React, { useState, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ALL_AVAILABLE_MODELS, FAL_IMAGE_MODELS, getProviderDisplayName } from '@/ai/models';
import type { InvalidModelEntry } from '@/domains/agents/utils/validate-agent-models';
import { BaseDialog } from '@/components/shared/base-dialog';

type ModelProvider = keyof typeof ALL_AVAILABLE_MODELS;
const PROVIDERS = Object.keys(ALL_AVAILABLE_MODELS) as ModelProvider[];

/** What the user has chosen for a single invalid entry */
interface Replacement {
  provider: ModelProvider;
  modelName: string;
}

interface InvalidAgentModelsDialogProps {
  open: boolean;
  invalidEntries: InvalidModelEntry[];
  onConfirm: (replacements: Map<string, Replacement>) => Promise<void>;
  onDismiss: () => void;
}

/** Stable key for each invalid entry */
function entryKey(e: InvalidModelEntry): string {
  return e.stepAgentId ? `${e.agent.id}::${e.stepAgentId}` : e.agent.id;
}

export function InvalidAgentModelsDialog({
  open,
  invalidEntries,
  onConfirm,
  onDismiss,
}: InvalidAgentModelsDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  // Default selections: first provider's first model (text) / first FAL model (image)
  const defaultProvider = PROVIDERS[0];
  const defaultModel = ALL_AVAILABLE_MODELS[defaultProvider][0].value;
  const defaultFalModel = FAL_IMAGE_MODELS[0].value;

  function defaultReplacementFor(entry: InvalidModelEntry): Replacement {
    // Use the agent definition's default model when available
    if (entry.suggestedProvider && entry.suggestedModelName) {
      return {
        provider: entry.suggestedProvider as ModelProvider,
        modelName: entry.suggestedModelName,
      };
    }
    if (entry.isImageModel) {
      return { provider: 'fal-ai' as ModelProvider, modelName: defaultFalModel };
    }
    return { provider: defaultProvider, modelName: defaultModel };
  }

  const [replacements, setReplacements] = useState<Map<string, Replacement>>(
    () => {
      const map = new Map<string, Replacement>();
      for (const entry of invalidEntries) {
        map.set(entryKey(entry), defaultReplacementFor(entry));
      }
      return map;
    },
  );

  // Re-initialise when entries change (dialog opens with new data)
  useMemo(() => {
    const map = new Map<string, Replacement>();
    for (const entry of invalidEntries) {
      map.set(entryKey(entry), defaultReplacementFor(entry));
    }
    setReplacements(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidEntries]);

  const updateReplacement = (key: string, partial: Partial<Replacement>) => {
    setReplacements((prev) => {
      const next = new Map(prev);
      const current = next.get(key) ?? {
        provider: defaultProvider,
        modelName: defaultModel,
      };
      const updated = { ...current, ...partial };
      // When provider changes, reset model to first in that provider
      if (partial.provider && partial.provider !== current.provider) {
        updated.modelName =
          ALL_AVAILABLE_MODELS[partial.provider][0].value;
      }
      next.set(key, updated);
      return next;
    });
  };

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await onConfirm(replacements);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BaseDialog
      isOpen={open}
      onOpenChange={(o) => !o && onDismiss()}
      maxWidth="2xl"
      title={
        <span className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          Invalid Agent Models Detected
        </span>
      }
      description="The following agents are configured with models that are no longer available. Please select a replacement model for each one, or dismiss to keep them as-is."
      cancelLabel="Dismiss"
      confirmLabel={`Update ${invalidEntries.length === 1 ? 'Model' : 'Models'}`}
      confirmLoading={isSaving}
      onConfirm={handleConfirm}
    >
      <ScrollArea className="max-h-[50vh]">
        <div className="space-y-6 pr-4">
          {invalidEntries.map((entry) => {
            const key = entryKey(entry);
            const replacement = replacements.get(key) ?? {
              provider: defaultProvider,
              modelName: defaultModel,
            };
            const modelOptions =
              ALL_AVAILABLE_MODELS[replacement.provider] ?? [];

            return (
              <div
                key={key}
                className="rounded-lg border p-4 space-y-3"
              >
                <div>
                  <p className="font-medium text-sm">{entry.agent.name}</p>
                  {entry.stepAgentId && (
                    <p className="text-xs text-muted-foreground">
                      Step override: {entry.stepAgentId}
                    </p>
                  )}
                  <p className="text-xs text-destructive mt-1">
                    Current:{' '}
                    <code className="bg-muted px-1 rounded">
                      {entry.invalidProvider}/{entry.invalidModelName}
                    </code>
                  </p>
                </div>

                {entry.isImageModel ? (
                  <div className="space-y-1">
                    <Label className="text-xs">Image Model</Label>
                    <Select
                      value={replacement.modelName}
                      onValueChange={(v) =>
                        updateReplacement(key, {
                          provider: 'fal-ai' as ModelProvider,
                          modelName: v,
                        })
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FAL_IMAGE_MODELS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Provider</Label>
                      <Select
                        value={replacement.provider}
                        onValueChange={(v) =>
                          updateReplacement(key, {
                            provider: v as ModelProvider,
                          })
                        }
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PROVIDERS.map((p) => (
                            <SelectItem key={p} value={p}>
                              {getProviderDisplayName(p)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Model</Label>
                      <Select
                        value={replacement.modelName}
                        onValueChange={(v) =>
                          updateReplacement(key, { modelName: v })
                        }
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {modelOptions.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </BaseDialog>
  );
}
